import { useEffect, useMemo, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import type {
  Composition,
  PromotionCategoryKey,
  PromotionEngineTab,
  PromotionKpis,
  PromotionVerdict
} from '../types';

export const engineCardCss = css`
  background: ${({ theme }) => theme.widget.background};
  border: 1px solid ${({ theme }) => theme.border_color};
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
`;

export const engineTableCss = css`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 12px 20px 12px 0;
    vertical-align: middle;
  }

  th {
    font-size: 0.78rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    text-align: right;
    white-space: nowrap;
    border-bottom: 1px solid ${({ theme }) => theme.border || 'rgba(0,0,0,0.08)'};
    color: ${({ theme }) => theme.text_muted || theme.text};
  }

  td {
    border-bottom: 1px solid ${({ theme }) => theme.border || 'rgba(0,0,0,0.06)'};
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 0.94rem;
    text-align: right;
    white-space: nowrap;
    color: ${({ theme }) => theme.text};
  }

  th:first-child,
  td:first-child {
    text-align: left;
  }

  td:first-child {
    font-family: inherit;
    font-weight: 600;
    white-space: normal;
    min-width: 160px;
  }

  th:last-child,
  td:last-child {
    padding-right: 0;
  }

  th.num,
  td.num {
    text-align: right;
    min-width: 88px;
  }

  th.locs,
  td.locs {
    text-align: left;
    font-family: inherit;
    white-space: nowrap;
    min-width: 140px;
  }

  tbody tr:last-child td {
    border-bottom: 0;
  }

  .pos {
    color: #16a34a;
  }

  .neg {
    color: #e11d48;
  }
`;

export const money = (value: number, currency: string) => {
  const abs = Math.abs(value);
  const formatted = abs.toLocaleString(undefined, {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0
  });
  const sign = value < 0 ? '-' : '';
  return `${sign}${currency} ${formatted}`;
};

export const pct = (value: number) => `${value.toFixed(1)}%`;

export const roi = (value: number) => `${value.toFixed(2)}x`;

export const isEmptyEnginePeriod = (kpis?: PromotionKpis | null) => {
  if (!kpis) return true;
  return (
    Number(kpis.orders || 0) === 0 &&
    Number(kpis.net_revenue || 0) === 0 &&
    Number(kpis.promo_discount || 0) === 0
  );
};

export const EmptyPeriodState = () => (
  <EmptyState>
    <EmptyTitle>No orders in this period</EmptyTitle>
    <EmptyBody>
      There are no completed orders for the selected location and dates. Try a wider range or
      another location.
    </EmptyBody>
  </EmptyState>
);

export const KpiStrip = ({
  kpis,
  currency
}: {
  kpis: PromotionKpis;
  currency: string;
}) => {
  const hasSpend = kpis.promo_discount > 0;
  const gmTone = kpis.gross_margin < 0 ? 'neg' : kpis.gross_margin > 0 ? 'pos' : undefined;
  const marginTone = kpis.margin_impact < 0 ? 'neg' : kpis.margin_impact > 0 ? 'pos' : undefined;
  const roiTone = hasSpend ? (kpis.promo_roi < 1 ? 'neg' : 'pos') : undefined;

  return (
    <KpiGrid>
      <Kpi>
        <KpiValue>{money(kpis.net_revenue, currency)}</KpiValue>
        <KpiLabel>Total revenue</KpiLabel>
        <KpiSub>net sales, selected period</KpiSub>
      </Kpi>
      <Kpi>
        <KpiValue>{money(kpis.promo_revenue, currency)}</KpiValue>
        <KpiLabel>Promotion revenue</KpiLabel>
        <KpiSub>
          {kpis.promo_orders.toLocaleString()} / {kpis.orders.toLocaleString()} orders
        </KpiSub>
      </Kpi>
      <Kpi $tone={gmTone}>
        <KpiValue>{money(kpis.gross_margin, currency)}</KpiValue>
        <KpiLabel>Gross margin</KpiLabel>
        <KpiSub>net sales − COGS</KpiSub>
      </Kpi>
      <Kpi>
        <KpiValue>{money(kpis.promo_discount, currency)}</KpiValue>
        <KpiLabel>Discount spend</KpiLabel>
        <KpiSub>promotion engine</KpiSub>
      </Kpi>
      <Kpi $tone={marginTone}>
        <KpiValue>{money(kpis.margin_impact, currency)}</KpiValue>
        <KpiLabel>Margin impact</KpiLabel>
        <KpiSub>promo-ticket GM after COGS</KpiSub>
      </Kpi>
      <Kpi>
        <KpiValue>{pct(kpis.penetration_pct)}</KpiValue>
        <KpiLabel>Promotion penetration</KpiLabel>
        <KpiSub>promo revenue ÷ total</KpiSub>
      </Kpi>
      <Kpi $tone={roiTone}>
        <KpiValue>{roi(kpis.promo_roi)}</KpiValue>
        <KpiLabel>ROI</KpiLabel>
        <KpiSub>promo GM ÷ discount</KpiSub>
      </Kpi>
      <Kpi>
        <KpiValue>{(kpis.total_promotions || 0).toLocaleString()}</KpiValue>
        <KpiLabel>Total promotions</KpiLabel>
        <KpiSub>catalog in this window</KpiSub>
      </Kpi>
      <Kpi $tone="pos">
        <KpiValue>{(kpis.performing_promotions || 0).toLocaleString()}</KpiValue>
        <KpiLabel>Performing</KpiLabel>
        <KpiSub>stars / earners / traffic</KpiSub>
      </Kpi>
      <Kpi $tone={(kpis.non_performing_promotions || 0) + (kpis.not_kicking_promotions || 0) > 0 ? 'neg' : undefined}>
        <KpiValue>
          {((kpis.non_performing_promotions || 0) + (kpis.not_kicking_promotions || 0)).toLocaleString()}
        </KpiValue>
        <KpiLabel>Need attention</KpiLabel>
        <KpiSub>
          {kpis.non_performing_promotions || 0} weak · {kpis.not_kicking_promotions || 0} not kicking
        </KpiSub>
      </Kpi>
    </KpiGrid>
  );
};

export const CompositionBar = ({ composition }: { composition: Composition }) => {
  const net = Number(composition?.net_revenue || 0);
  const fullPct = Number(composition?.full_price_pct || 0);
  const promoPct = Number(composition?.promo_pct || 0);
  const segments = [
    { kind: 'full' as const, value: fullPct, name: 'Full-price' },
    { kind: 'promo' as const, value: promoPct, name: 'Promo' }
  ].filter((s) => s.value >= 1);
  const showEmpty = net <= 0 || segments.length === 0;

  return (
    <CompWrap>
      <CompBar>
        {showEmpty ? (
          <EmptySeg>No revenue in this period</EmptySeg>
        ) : (
          segments.map((s) => (
            <Seg key={s.kind} $flex={s.value} $kind={s.kind} $labeled={s.value >= 14}>
              {s.value >= 14 && (
                <>
                  <SegPct>{pct(s.value)}</SegPct>
                  <SegName>{s.name}</SegName>
                </>
              )}
            </Seg>
          ))
        )}
      </CompBar>
      <Legend>
        <span>
          <Dot $kind="full" /> Full-price
        </span>
        <span>
          <Dot $kind="promo" /> Promo-touched revenue
        </span>
      </Legend>
    </CompWrap>
  );
};

export const CompositionDonut = ({
  composition,
  currency
}: {
  composition: Composition;
  currency: string;
}) => {
  const promoPct = Math.max(0, Math.min(100, Number(composition?.promo_pct || 0)));
  const fullPct = Math.max(0, 100 - promoPct);

  return (
    <DonutWrap>
      <DonutRing $promo={promoPct}>
        <DonutHole>
          <DonutPct>{pct(promoPct)}</DonutPct>
          <DonutCap>promo mix</DonutCap>
        </DonutHole>
      </DonutRing>
      <DonutLegend>
        <DonutRow>
          <Dot $kind="promo" />
          <span>Promotion revenue</span>
          <b>
            {pct(promoPct)} · {money(composition.promo_revenue, currency)}
          </b>
        </DonutRow>
        <DonutRow>
          <Dot $kind="full" />
          <span>Revenue without promotion</span>
          <b>
            {pct(fullPct)} · {money(composition.full_price_revenue, currency)}
          </b>
        </DonutRow>
      </DonutLegend>
    </DonutWrap>
  );
};

export const VerdictBadge = ({ verdict }: { verdict: PromotionVerdict }) => (
  <Badge $key={verdict.key}>{verdict.label}</Badge>
);

export const CategoryBadge = ({
  categoryKey,
  label
}: {
  categoryKey: PromotionCategoryKey;
  label: string;
}) => <Badge $key={categoryKey}>{label}</Badge>;

export const LocationChips = ({
  names,
  count
}: {
  names?: string[] | null;
  count: number;
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const list = useMemo(
    () => (names || []).map((n) => n.trim()).filter(Boolean),
    [names]
  );
  const total = Math.max(count || 0, list.length);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((n) => n.toLowerCase().includes(q));
  }, [list, query]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  if (total <= 1) {
    return <LocName title={list[0]}>{list[0] || '—'}</LocName>;
  }

  return (
    <LocMenu ref={rootRef}>
      <LocTrigger
        type="button"
        aria-expanded={open}
        onClick={() => {
          setOpen((v) => !v);
          setQuery('');
        }}
      >
        {total.toLocaleString()} locations
        <Caret $open={open} />
      </LocTrigger>
      {open && (
        <LocPanel>
          {list.length > 8 && (
            <LocSearch
              autoFocus
              value={query}
              placeholder="Search locations"
              onChange={(e) => setQuery(e.target.value)}
            />
          )}
          <LocList>
            {filtered.length === 0 ? (
              <LocEmpty>No matches</LocEmpty>
            ) : (
              filtered.map((name) => <li key={name}>{name}</li>)
            )}
          </LocList>
        </LocPanel>
      )}
    </LocMenu>
  );
};

const KpiSkeleton = () => (
  <KpiGrid>
    {Array.from({ length: 10 }).map((_, i) => (
      <SkelCard key={`kpi-${i}`}>
        <Shimmer $width="58%" $height="22px" />
        <Shimmer $width="72%" $height="12px" />
        <Shimmer $width="44%" $height="10px" />
      </SkelCard>
    ))}
  </KpiGrid>
);

const TableRowSkeleton = () => (
  <TableRow>
    <Shimmer $width="100%" $height="12px" />
    <Shimmer $width="70%" $height="12px" />
    <Shimmer $width="50%" $height="12px" />
    <Shimmer $width="60%" $height="12px" />
    <Shimmer $width="55%" $height="12px" />
    <Shimmer $width="45%" $height="12px" />
    <Shimmer $width="40%" $height="12px" />
  </TableRow>
);

const ReportTableSkeleton = ({ rows = 8 }: { rows?: number }) => (
  <SkelPanel>
    <TableRow>
      {Array.from({ length: 7 }).map((_, i) => (
        <Shimmer key={`h-${i}`} $width="70%" $height="10px" />
      ))}
    </TableRow>
    {Array.from({ length: rows }).map((_, i) => (
      <TableRowSkeleton key={`r-${i}`} />
    ))}
  </SkelPanel>
);

export const EngineSkeleton = ({ tab = 'overview' }: { tab?: PromotionEngineTab }) => {
  if (tab === 'report') {
    return (
      <SkeletonWrap aria-busy="true" aria-live="polite">
        <ReportTableSkeleton rows={8} />
      </SkeletonWrap>
    );
  }

  if (tab === 'performance') {
    return (
      <SkeletonWrap aria-busy="true" aria-live="polite">
        <SkelPanel>
          <SkeletonRow>
            <HeroCol>
              <Shimmer $width="40%" $height="12px" />
              <Shimmer $width="52%" $height="44px" />
              <Shimmer $width="88%" $height="12px" />
            </HeroCol>
            <HeroCol>
              <Shimmer $width="48%" $height="12px" />
              <Shimmer $width="100%" $height="56px" />
            </HeroCol>
          </SkeletonRow>
        </SkelPanel>
        <ReportTableSkeleton rows={6} />
      </SkeletonWrap>
    );
  }

  return (
    <SkeletonWrap aria-busy="true" aria-live="polite">
      <KpiSkeleton />
      <SkeletonRow>
        <SkelPanel>
          <Shimmer $width="40%" $height="14px" />
          <Shimmer $width="100%" $height="56px" />
          <Shimmer $width="70%" $height="12px" />
        </SkelPanel>
        <SkelPanel>
          <Shimmer $width="36%" $height="14px" />
          <Shimmer $width="100%" $height="12px" />
          <Shimmer $width="92%" $height="12px" />
          <Shimmer $width="80%" $height="12px" />
          <Shimmer $width="88%" $height="12px" />
        </SkelPanel>
      </SkeletonRow>
      <ReportTableSkeleton rows={5} />
    </SkeletonWrap>
  );
};

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  @media (max-width: 640px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const Kpi = styled.div<{ $tone?: 'pos' | 'neg' }>`
  ${engineCardCss}
  border-radius: 12px;
  padding: 14px;
  border-left: 3px solid
    ${({ $tone, theme }) =>
      $tone === 'neg' ? '#e11d48' : $tone === 'pos' ? '#16a34a' : theme.primary_color || '#0d8a6a'};
`;

const KpiValue = styled.div`
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-weight: 700;
  font-size: 1.38rem;
  color: ${({ theme }) => theme.text};
`;

const KpiLabel = styled.div`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.text_secondary || theme.text};
  margin-top: 4px;
  font-weight: 600;
`;

const KpiSub = styled.div`
  font-size: 0.78rem;
  color: ${({ theme }) => theme.text_muted || theme.text_secondary || theme.text};
  margin-top: 4px;
  opacity: 0.8;
`;

const CompWrap = styled.div``;

const EmptyState = styled.div`
  ${engineCardCss}
  text-align: center;
  padding: 72px 24px;
  border-radius: 14px;
`;

const EmptyTitle = styled.h3`
  margin: 0 0 8px;
  font-size: 1.05rem;
  font-weight: 700;
  color: ${({ theme }) => theme.text};
`;

const EmptyBody = styled.p`
  margin: 0 auto;
  max-width: 42ch;
  font-size: 0.88rem;
  line-height: 1.45;
  color: ${({ theme }) => theme.text_muted || theme.text_secondary || theme.text};
`;

const CompBar = styled.div`
  display: flex;
  height: 56px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.border_color};
  background: ${({ theme }) => theme.widget_group.background};
`;

const EmptySeg = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 600;
  color: ${({ theme }) => theme.text_muted || theme.text};
`;

const Seg = styled.div<{ $flex: number; $kind: 'full' | 'promo'; $labeled: boolean }>`
  flex: ${({ $flex }) => `${$flex} ${$flex} 0`};
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: ${({ $labeled }) => ($labeled ? '0 14px' : '0')};
  min-width: 0;
  overflow: hidden;
  background: ${({ $kind }) =>
    $kind === 'full' ? 'rgba(91, 127, 176, 0.18)' : 'rgba(240, 52, 127, 0.14)'};
  color: ${({ theme }) => theme.text};
`;

const SegPct = styled.div`
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-weight: 700;
  font-size: 1rem;
`;

const SegName = styled.div`
  font-size: 0.68rem;
  font-weight: 600;
  opacity: 0.85;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Legend = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 10px;
  flex-wrap: wrap;
  font-size: 0.78rem;
  color: ${({ theme }) => theme.text_secondary || theme.text};

  span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
`;

const Dot = styled.i<{ $kind: 'full' | 'promo' }>`
  width: 9px;
  height: 9px;
  border-radius: 3px;
  display: inline-block;
  background: ${({ $kind }) => ($kind === 'full' ? '#5B7FB0' : '#F0347F')};
`;

const DonutWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 22px;
  flex-wrap: wrap;
`;

const DonutRing = styled.div<{ $promo: number }>`
  width: 148px;
  height: 148px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $promo }) =>
    `conic-gradient(#f0347f 0 ${$promo}%, rgba(91, 127, 176, 0.85) ${$promo}% 100%)`};
  display: grid;
  place-items: center;
`;

const DonutHole = styled.div`
  width: 92px;
  height: 92px;
  border-radius: 50%;
  background: ${({ theme }) => theme.widget.background};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const DonutPct = styled.div`
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-weight: 800;
  font-size: 1.15rem;
  color: ${({ theme }) => theme.text};
`;

const DonutCap = styled.div`
  font-size: 0.72rem;
  font-weight: 600;
  color: ${({ theme }) => theme.text_muted || theme.text};
`;

const DonutLegend = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 220px;
  flex: 1;
`;

const DonutRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.88rem;
  color: ${({ theme }) => theme.text};

  span {
    flex: 1;
  }

  b {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-weight: 700;
    white-space: nowrap;
  }
`;

const shimmer = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

const SkeletonWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const SkeletonRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const SkelCard = styled.div`
  ${engineCardCss}
  border-radius: 12px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SkelPanel = styled.div`
  ${engineCardCss}
  border-radius: 14px;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const HeroCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 1fr 0.7fr 0.9fr 0.9fr 0.9fr 0.7fr;
  gap: 16px;
  align-items: center;
`;

const Shimmer = styled.div<{ $width: string; $height: string }>`
  width: ${({ $width }) => $width};
  height: ${({ $height }) => $height};
  border-radius: 6px;
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.widget_group.background} 25%,
    ${({ theme }) => theme.border_color} 50%,
    ${({ theme }) => theme.widget_group.background} 75%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.3s ease-in-out infinite;
`;

const LocName = styled.span`
  display: inline-block;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: inherit;
  font-size: 0.82rem;
  font-weight: 600;
`;

const LocMenu = styled.div`
  position: relative;
  display: inline-block;
`;

const LocTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: inherit;
  font-size: 0.74rem;
  font-weight: 700;
  line-height: 1.2;
  padding: 4px 8px;
  border-radius: 999px;
  cursor: pointer;
  color: ${({ theme }) => theme.text};
  background: ${({ theme }) => theme.widget_group.background};
  border: 1px solid ${({ theme }) => theme.border_color};
`;

const Caret = styled.i<{ $open: boolean }>`
  display: inline-block;
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 5px solid currentColor;
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'none')};
  opacity: 0.7;
`;

const LocPanel = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 40;
  min-width: 220px;
  max-width: 280px;
  max-height: 260px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 10px;
  background: ${({ theme }) => theme.widget.background};
  border: 1px solid ${({ theme }) => theme.border_color};
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.18);
`;

const LocSearch = styled.input`
  margin: 8px 8px 0;
  padding: 6px 8px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.border_color};
  background: ${({ theme }) => theme.body};
  color: ${({ theme }) => theme.text};
  font-size: 0.78rem;
  outline: none;
`;

const LocList = styled.ul`
  margin: 0;
  padding: 6px 0;
  overflow-y: auto;
  max-height: 220px;
  list-style: none;

  li {
    padding: 6px 12px;
    font-size: 0.78rem;
    font-weight: 600;
    color: ${({ theme }) => theme.text};
  }
`;

const LocEmpty = styled.li`
  opacity: 0.7;
  font-weight: 500 !important;
`;

const Badge = styled.span<{ $key: string }>`
  display: inline-block;
  font-size: 0.78rem;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 20px;
  color: ${({ $key }) =>
    $key === 'ok' || $key === 'stars' || $key === 'quiet_earners'
      ? '#15803d'
      : $key === 'grow' || $key === 'traffic_builders'
        ? '#0369a1'
        : $key === 'leak' || $key === 'margin_loss' || $key === 'underperforming'
          ? '#be123c'
          : $key === 'over' || $key === 'overperforming'
            ? '#a16207'
            : $key === 'idle' || $key === 'not_kicking'
              ? '#57534e'
              : '#c2410c'};
  background: ${({ $key }) =>
    $key === 'ok' || $key === 'stars' || $key === 'quiet_earners'
      ? 'rgba(22,163,74,0.12)'
      : $key === 'grow' || $key === 'traffic_builders'
        ? 'rgba(14,165,233,0.12)'
        : $key === 'leak' || $key === 'margin_loss' || $key === 'underperforming'
          ? 'rgba(225,29,72,0.12)'
          : $key === 'over' || $key === 'overperforming'
            ? 'rgba(202,138,4,0.16)'
            : $key === 'idle' || $key === 'not_kicking'
              ? 'rgba(120,113,108,0.14)'
              : 'rgba(234,88,12,0.12)'};
`;
